'use client';

import React, { useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Character } from '../types';
import { CharacterFormContent } from './CharacterFormContent';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet';

interface CharacterFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  character?: Character | null;
  onSuccess: (character: Character) => void;
}

export function CharacterForm({
  open,
  onOpenChange,
  character,
  onSuccess,
}: CharacterFormProps) {
  const t = useTranslations('stories.characters.form');
  const [isBusy, setIsBusy] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    // Block close while busy (portrait generation / cost dialog / mutation in progress)
    if (!newOpen && isBusy) return;
    onOpenChange(newOpen);
  };

  const handleBusyChange = useCallback((busy: boolean) => {
    setIsBusy(busy);
  }, []);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        data-testid="character-form-sheet"
        side="right"
        className="w-full sm:max-w-2xl"
        onInteractOutside={(e) => { if (isBusy) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isBusy) e.preventDefault(); }}
      >
        <SheetHeader>
          <SheetTitle>
            {character ? t('editTitle') : t('createTitle')}
          </SheetTitle>
          <SheetDescription>
            {character ? t('editDescription') : t('createDescription')}
          </SheetDescription>
        </SheetHeader>

        <CharacterFormContent
          character={character}
          onSuccess={onSuccess}
          onCancel={() => onOpenChange(false)}
          isActive={open}
          onBusyChange={handleBusyChange}
        />
      </SheetContent>
    </Sheet>
  );
}
